import { usePrivy, useLoginWithSiwe, type User } from "@privy-io/react-auth";
import { useWallets } from "@privy-io/react-auth/solana";
import { MiniKit } from "@worldcoin/minikit-js";
import { Permission } from "@worldcoin/minikit-js/commands";
import { useCreateWallet } from "@privy-io/react-auth/solana";
import { toast } from "sonner";
import { useCallback } from "react";
import { useLocalNetworkAccess } from "./useLocalNetworkAccess";

export function usePrivyCustom() {
  const { user: privyUser, login: privyLogin, ...rest } = usePrivy();
  const { wallets } = useWallets();
  const { createWallet } = useCreateWallet();

  let user: User | null = privyUser
    ? {
        ...privyUser,
        wallet: privyUser.wallet
          ? {
              ...privyUser?.wallet,
              address: wallets?.[0]?.address ?? null,
            }
          : undefined,
      }
    : null;

  const { generateSiweNonce, loginWithSiwe } = useLoginWithSiwe({
    onComplete: async (params) => {
      if (params.isNewUser) {
        try {
          await createWallet();
        } catch {
          toast.error("Failed to create embedded wallet");
        }
      }

      // Request World notification permission after login (non-blocking)
      if (MiniKit.isInWorldApp()) {
        try {
          await MiniKit.requestPermission({
            permission: Permission.Notifications,
          });
        } catch {
          // User denied or permission already granted — either way, login continues
        }
      }
    },
  });

  const lna = useLocalNetworkAccess();

  const login = useCallback(async () => {
    if (!MiniKit.isInWorldApp()) {
      if (lna) {
        try {
          await lna.ensurePermission();
        } catch {
          return; // User dismissed or permission denied
        }
      }
      privyLogin();
      return;
    }
    // Get nonce from Privy
    const privyNonce = await generateSiweNonce();

    // Request signature from World wallet
    const { data } = await MiniKit.walletAuth({
      nonce: privyNonce,
    });

    // Log in with Privy
    await loginWithSiwe({
      message: data.message,
      signature: data.signature,
      walletClientType: MiniKit.isInWorldApp() ? "world" : undefined,
    });
  }, [generateSiweNonce, loginWithSiwe, privyLogin, lna]);

  return { ...rest, login, user };
}
